'use client';

import { useEffect, useState } from 'react';

interface IPDFViewerProps {
  pdfUrl: string;
  title?: string;
}

/**
 * -----------------------------------------------------------------------------
 * This component displays a PDF viewer using an iframe.
 * It maintains a responsive aspect ratio for optimal viewing.
 */
export function PDFViewer({ pdfUrl, title }: IPDFViewerProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
  }, [pdfUrl]);

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  return (
    <div className="relative w-full" style={{ minHeight: '800px' }}>
      {isLoading ? (
        <div
          className="
            absolute inset-0 z-10 flex items-center justify-center
            rounded-lg border bg-gray-50 dark:bg-gray-900
          "
          style={{ minHeight: '800px' }}
        >
          <div className="text-center">
            <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent" />
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Loading PDF...
            </p>
          </div>
        </div>
      ) : null}

      {hasError ? (
        <div
          className="
            absolute inset-0 z-10 flex items-center justify-center
            rounded-lg border bg-gray-50 dark:bg-gray-900
          "
          style={{ minHeight: '800px' }}
        >
          <div className="text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Unable to load PDF. Please try again later.
            </p>
          </div>
        </div>
      ) : null}

      <iframe
        className="h-full w-full rounded-lg border"
        onError={handleError}
        onLoad={handleLoad}
        src={pdfUrl}
        style={{
          minHeight: '800px',
          opacity: isLoading || hasError ? 0 : 1,
          transition: 'opacity 0.3s ease-in-out',
        }}
        title={title || 'PDF Document'}
      />
    </div>
  );
}
